<template>
  <div>
    <div class="summary-header">
      <div class="summary-title">
        <div class="summary-icon"></div>
        <div class="summary-text">{{ $t('samples') }}</div>
      </div>
    </div>

    <DxDataGrid
      :data-source="store"
      :show-borders="false"
      :show-column-lines="false"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :column-auto-width="true"
      :show-column-headers="true"
      :show-colon="false"
      :remote-operations="true"
      :hover-state-enabled="false"
      :show-row-lines="false"
      :row-alternation-enabled="false"
      :word-wrap-enabled="true"
      :no-data-text="$t('noDataTextSummary')"
      class="custom-grid custom-grid-summary"
      @exporting="onExporting"
      @cell-prepared="onCellPrepared"
      @context-menu-preparing="addMenuItems"
      @content-ready="init"
      @toolbar-preparing="onToolbarPreparing($event)"
      @data-error-occurred="onGridError"
    >
      <DxScrolling mode="standard" />
      <DxStateStoring
        :enabled="true"
        type="custom"
        storage-key="gridAllHomeSamples"
        :custom-load="loadState"
        :custom-save="customSave"
      />
      <DxPager
        :show-page-size-selector="true"
        :allowed-page-sizes="[5, 10, 20]"
        :show-info="true"
        info-text="{2} items"
        :visible="true"
      />
      <DxExport :enabled="false" />
      <DxSearchPanel
        :visible="true"
        :placeholder="$t('searchSampleCode')"
      />
      <!-- <DxColumn
        data-field="summary_semaphore"
        caption="marcadores"
        :width="55"
        :allow-sorting="true"
        cell-template="cellTemplateDots"
        :fixed="true"
        fixed-position="left"
      /> -->
      <DxColumn
        :caption="$t('markers')"
        :fixed="true"
        fixed-position="left"
        :allow-sorting="false"
        :visible-index="0"
        cell-template="cellTemplateCombined"
        :min-width="120"
      />
      <DxColumn
        data-field="code"
        :caption="$t('code')"
        alignment="center"
        :allow-header-filtering="false"
        :visible-index="1"
        :fixed="true"
        :min-width="120"
      />
      <DxColumn
        data-field="analyse_type"
        :caption="$t('foodSamples.analysisType')"
        :allow-header-filtering="false"
        :visible-index="2"
        :min-width="135"
      />
      <DxColumn
        data-field="status"
        :caption="$t('status')"
        alignment="center"
        :min-width="90"
        :allow-header-filtering="false"
        :visible-index="3"
        cell-template="cellTemplateStatus"
      />
      <DxColumn
        data-field="reception_date"
        :caption="$t('foodSamples.receptionDate')"
        alignment="center"
        data-type="date"
        :format="$helpers.getDateFormat(company)"
        :sort-order="'desc'"
        :allow-header-filtering="false"
        :visible-index="4"
        :word-wrap-enabled="true"
        :width="130"
      />
      <DxColumn
        data-field="end_date"
        :caption="$t('foodSamples.endDate')"
        alignment="center"
        data-type="date"
        :format="$helpers.getDateFormat(company)"
        :allow-header-filtering="false"
        :visible-index="5"
      />
      <!-- <DxColumn
        data-field="atc_name"
        :caption="$t('atc')"
        alignment="left"
        :allow-header-filtering="false"
      /> -->
      <DxColumn
        data-field="sample_type"
        :caption="$t('agronomySample.summary.sampleType')"
        alignment="center"
        :allow-header-filtering="false"
        :visible-index="6"
      />
      <DxColumn
        data-field="sample_name"
        :caption="$t('agronomySample.summary.description')"
        alignment="left"
        :allow-header-filtering="false"
        :visible-index="7"
      />

      <DxColumn data-field="id" :visible="false" />
      <DxColumn
        name="buttons"
        :buttons="updatedEditButtons"
        :fixed="true"
        fixed-position="right"
        type="buttons"
        width="80"
        :visible-index="7"
      />
      <template #cellTemplateCombined="{ data }">
        <div class="combined-status-cell">
          <img
            v-if="data.data.summary_semaphore !== null"
            width="20"
            height="20"
            :src="getDotColorImage(data.data.summary_semaphore)"
            class="status-dot"
          />
          <span
            :class="
              $helpers.getStatusAlertClass(
                data.data.summary_semaphore
              )
            "
            class="status-text marker-text"
          >
            {{
              {
                green: $t('newInfoSemaphore.greenDotDescription'),
                orange: $t('newInfoSemaphore.orangeDotDescription'),
                grey: $t('newInfoSemaphore.greyDotDescription'),
                red: $t('newInfoSemaphore.redDotDescription'),
              }[data.data.summary_semaphore] || data.data.status
            }}
          </span>
        </div>
      </template>

      <template #cellTemplateStatus="{ data }">
        <div
          :class="$helpers.getStatusClass(data.data.status_code)"
          class="status-text"
          style="text-align: center"
        >
          {{ data.data.status }}
        </div>
      </template>
    </DxDataGrid>

    <!-- <ContextMenu :id="'context-menu-btn'" :items="getItems" /> -->

    <NotificationSpinner
      v-if="notificationMessage.length > 0"
      :message="notificationMessage"
    />
  </div>
</template>
<script>
import { mapActions, mapState } from 'vuex';
import {
  DxDataGrid,
  DxColumn,
  DxSearchPanel,
  DxStateStoring,
  DxPager,
  DxPaging,
  DxScrolling,
  DxExport,
} from 'devextreme-vue/data-grid';

import saveAs from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import { trigger } from 'devextreme/events';
import NotificationSpinner from '@/components/NotificationSpinner/index.vue';
import { NOTIFY_TIME_LONG } from '@/js/constants';
import { downloadReportMixin } from '@/mixins/downloadReportMixin';
import iconVisibility from '@/static/icons/ic_visibility.svg';
import iconDownload from '@/static/icons/ic_download.svg';
import { getGridActionCellElement } from '@/js/gridActionCell';
import { resolveAtcKey } from '@/utils/atcRouting';

const DOC_TYPE_TRACES = 2;

export default {
  components: {
    DxDataGrid,
    DxColumn,
    DxSearchPanel,
    DxStateStoring,
    DxPager,
    DxPaging,
    DxScrolling,
    DxExport,
    NotificationSpinner,
  },
  mixins: [downloadReportMixin],
  props: {
    store: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      // filtros de los botones de la toolbar
      isFilterActive: {
        inProcess: false,
        finished: false,
        cancelled: false,
        lastMonth: false,
        onAlert: false, // 🔴🟠 rojo o naranja
        noPositives: false, // 🟢 NUEVO
        noReviewed: false,
      },
      editButtons: [
        {
          icon: iconVisibility,
          onClick: this.viewAtc,
          cssClass: 'grid-button-icon',
        },
        {
          icon: iconDownload,
          onClick: this.download,
          cssClass: 'grid-button-icon',
        },
      ],
      types: {},
      itemsDisabled: [
        {
          icon: 'excel',
          text: this.$t('excelDownload'),
          onItemClick: () => {
            this.onExporting(this.grid);
          },
        },
        {
          icon: 'pdf',
          text: this.$t('pdfReportDownload'),
          onItemClick: () => {
            // this.onExporting(this.grid);
          },
          disabled: true,
        },
      ],
      itemsEnabled: [
        {
          icon: 'excel',
          text: this.$t('excelDownload'),
          onItemClick: () => {
            this.onExporting(this.grid);
          },
        },
        {
          icon: 'pdf',
          text: this.$t('pdfReportDownload'),
          onItemClick: async () => {
            this.setDisableContext(true);

            try {
              this.notificationMessage = this.$t('downloading');
              const b64 = await this.getSamplesZip(
                this.getSamplesPageDownloadParams()
              );

              const link = document.createElement('a');
              link.href = `data:application/octet-stream;base64,${b64}`;
              link.download = `besafer-reports-PDF-${new Date().toLocaleString()}.zip`;
              link.click();

              URL.revokeObjectURL(link.href);
              this.notificationMessage = this.$t('downloadedFile');
            } catch (e) {
              this.notificationMessage = '';
              this.$notifyDX(
                {
                  message: this.$t(
                    this.$helpers.getFilteredErrorMessage(
                      JSON.parse(e.message).error
                    )
                  ),
                  width: 550,
                },
                'error',
                NOTIFY_TIME_LONG
              );
            } finally {
              this.setDisableContext(false);

              setTimeout(() => {
                this.notificationMessage = '';
              }, 2000);
            }
          },
        },
      ],
      grid: null, // aquí guardamos el evento de content-ready
      notificationMessage: '',
    };
  },
  computed: {
    getItems() {
      return this.disableContext
        ? this.itemsDisabled
        : this.itemsEnabled;
    },
    ...mapState('contact', ['company']),
    ...mapState('samples', ['disableContext', 'isDownloadingReport']),
    ...mapState('agronomySamples', {
      allAgronomySamples: 'allSamples',
    }),
    ...mapState('foodSamples', {
      allFoodSamples: 'allSamples',
    }),
    ...mapState('environmentSamples', {
      allEnvironmentSamples: 'allSamples',
    }),
    ...mapState('miningSamples', {
      allMiningSamples: 'allSamples',
    }),
    ...mapState('healthSecuritySamples', {
      allHealthSecuritySamples: 'allSamples',
    }),
    ...mapState('samplesRegisterModule', ['invoiceSamples']),
    updatedEditButtons() {
      return this.isDownloadingReport
        ? [
            {
              icon: iconVisibility,
              onClick: this.viewAtc,
              cssClass: 'grid-button-icon',
            },
            {
              icon: iconDownload,
              onClick: this.download,
              cssClass: 'grid-button-icon',
              disabled: true,
            },
          ]
        : [
            {
              icon: iconVisibility,
              onClick: this.viewAtc,
              cssClass: 'grid-button-icon',
            },
            {
              icon: iconDownload,
              onClick: this.download,
              disabled: this.isDisabled,
              cssClass: 'grid-button-icon',
            },
          ];
    },
  },

  beforeMount() {
    this.setIsDownloadingReport(false);
    this.setDisableContext(false);

    // Leer filtros guardados de localStorage
    const raw = localStorage.getItem('gridallHomeSamples');
    if (raw) {
      try {
        const saved = JSON.parse(raw);
        if (saved.isFilterActive) {
          Object.assign(this.isFilterActive, saved.isFilterActive);
        }
      } catch (e) {
        console.warn(
          'gridallHomeSamples corrupto en localStorage',
          e
        );
      }
    }

    const hasAnyFilter =
      this.isFilterActive.inProcess ||
      this.isFilterActive.finished ||
      this.isFilterActive.cancelled ||
      this.isFilterActive.lastMonth ||
      this.isFilterActive.noReviewed ||
      this.isFilterActive.onAlert ||
      this.isFilterActive.noPositives;

    if (hasAnyFilter) {
      this.applyFilters();
    } else {
      this.setAllFilterParams({ atc_name: '' });
    }
  },

  methods: {
    onGridError(error) {
      // console.error('Error del grid:', error);

      this.$notifyDX(
        {
          message: this.$t('internalServerError'),
          width: 550,
        },
        'error',
        NOTIFY_TIME_LONG
      );
    },
    getLastMonthDates() {
      const endDate = new Date(); // Hoy
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30); // Hoy menos 30 días

      const formatDate = (date) => {
        const d = new Date(date);
        let month = `${d.getMonth() + 1}`;
        let day = `${d.getDate()}`;
        const year = d.getFullYear();

        if (month.length < 2) month = `0${month}`;
        if (day.length < 2) day = `0${day}`;

        return [year, month, day].join('-');
      };

      return {
        receptionDate_after: formatDate(startDate),
        receptionDate_before: formatDate(endDate),
      };
    },

    applyFilters() {
      const params = {
        receptionDate_after: '',
        receptionDate_before: '',
        endDate_after: '',
        endDate_before: '',
        sample_type: '',
        analyse_type: '',
        atc_name: '',
        not_seen: '',
        summary_semaphore: '', // 🔴🟠 NUEVO
      };

      // Último mes
      if (this.isFilterActive.lastMonth) {
        const { receptionDate_after, receptionDate_before } =
          this.getLastMonthDates();
        params.endDate_after = receptionDate_after;
        params.endDate_before = receptionDate_before;
      }

      // Estados
      const statuses = [];
      if (this.isFilterActive.inProcess) statuses.push(2);
      if (this.isFilterActive.finished) statuses.push(3);
      if (this.isFilterActive.cancelled) statuses.push(4);

      if (statuses.length > 0) {
        params.status = statuses.join(','); // "2,3,4"
      }

      // 🟡🔴🟢 Semáforos (ACUMULATIVO)
      const semaphores = [];

      if (this.isFilterActive.onAlert) {
        semaphores.push('red', 'orange');
      }

      if (this.isFilterActive.noPositives) {
        semaphores.push('green');
      }

      if (semaphores.length > 0) {
        params.summary_semaphore = semaphores.join(',');
      }
      if (this.isFilterActive.noReviewed) {
        params.not_seen = true;
      }
      this.setCurrentFilter('home');
      this.setAllFilterParams(params);
      this.$store.dispatch(
        'homeSamples/callStoreWithFilters',
        params,
        { root: true }
      );
      this.saveGridState();
    },

    // Guarda el estado de la grid + filtros
    saveGridState() {
      if (!this.grid || !this.grid.component) return;
      const gridState = this.grid.component.state();
      this.customSave(gridState);
    },

    loadState() {
      let state = localStorage.getItem('gridallHomeSamples');
      if (!state) return null;

      state = JSON.parse(state);

      // No dejamos que DevExtreme reciba nuestro campo custom
      if (state.isFilterActive) {
        delete state.isFilterActive;
      }

      state.searchText = '';
      return state;
    },

    customSave(state) {
      // Descomentar para guardar estados de burbuja en grid
      const extendedState = {
        ...state,
        isFilterActive: this.isFilterActive,
      };
      localStorage.setItem(
        'gridallHomeSamples',
        JSON.stringify(extendedState)
      );
    },

    getDotColorImage(color) {
      return require(`../../../../assets/dot-colors/${color}_new.svg`)
        .default;
    },

    init(e) {
      this.setSamples(e.component?.getDataSource()?.items());
      this.grid = e; // guardamos el evento (e.component es la instancia)
    },

    formatExcel(samples) {
      const config = this.$helpers.buildSamplesParametersExcelConfig(
        samples,
        [
          {
            label: this.$t('agronomySample.summary.sampleCode'),
            value: 'code',
          },
          {
            label: this.$t('agronomySample.summary.description'),
            value: 'report_observations',
          },
          {
            label: this.$t('foodSamples.receptionDate'),
            value: 'reception_date',
          },
        ]
      );

      const fileSettings = {
        fileName: `besafer-samples-parameters-${this.$helpers.getDateFormatedDayMonthYear(
          new Date()
        )}`,
        extraLength: 3,
        writeOptions: {},
      };

      this.$jsonToXLSX(config, fileSettings, () => {
        this.setDisableContext(false);

        this.$notifyDX(
          {
            message: this.$t('xslxDownloadedSuccessfully'),
            width: 400,
          },
          'success',
          2000
        );
      });
    },

    GoSampleRegister() {
      try {
        if (this.invoiceSamples.length > 0) {
          this.setInvoiceSamples([]);
        }
        this.RegisterInvoice();
        this.$f7router.navigate(
          {
            name: 'SamplesRegister',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(error.message)
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      }
    },

    onToolbarPreparing(e) {
      const toolbarItems = e.toolbarOptions.items;
      e.toolbarOptions.elementAttr = {
        class: 'custom-toolbar',
      };
      toolbarItems.forEach((item) => {
        if (item.name === 'searchPanel') {
          item.location = 'before';
        }
      });

      // Botón Filtros (panel derecho)
      toolbarItems.push({
        name: 'Filtros',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('filter'),
          icon: '././static/icons/ic_filter.svg',
          width: 115,
          elementAttr: {
            class: 'btn-filtrar',
          },
          onClick: () => {
            this.$f7.panel.open('right');
          },
        },
      });

      toolbarItems.push({
        name: 'En alerta',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('onAlert'),
          icon: '././static/icons/ic_check.svg',
          width: 205,
          elementAttr: {
            class: `filtrosBotones ${
              this.isFilterActive.onAlert ? 'show-icon' : 'hide-icon'
            }`,
          },
          // onClick: (e) => {
          //   e.element.classList.toggle('show-icon');
          // },
          onClick: (e) => {
            this.isFilterActive.onAlert =
              !this.isFilterActive.onAlert;

            e.element.classList.toggle(
              'show-icon',
              this.isFilterActive.onAlert
            );
            e.element.classList.toggle(
              'hide-icon',
              !this.isFilterActive.onAlert
            );

            this.applyFilters();
          },
        },
      });

      // Sin revisar
      toolbarItems.push({
        name: 'Sin revisar',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('notReviewed'),
          icon: '././static/icons/ic_check.svg',
          width: 170,
          elementAttr: {
            class: `filtrosBotones ${
              this.isFilterActive.noReviewed
                ? 'show-icon'
                : 'hide-icon'
            }`,
          },
          onClick: (e) => {
            this.isFilterActive.noReviewed =
              !this.isFilterActive.noReviewed;

            e.element.classList.toggle(
              'show-icon',
              this.isFilterActive.noReviewed
            );
            e.element.classList.toggle(
              'hide-icon',
              !this.isFilterActive.noReviewed
            );

            this.applyFilters();
          },
        },
      });

      // Sin positivos
      toolbarItems.push({
        name: 'Sin positivos',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('noPositives'),
          icon: '././static/icons/ic_check.svg',
          width: 150,
          elementAttr: {
            class: `filtrosBotones ${
              this.isFilterActive.noPositives
                ? 'show-icon'
                : 'hide-icon'
            }`,
          },
          // onClick: (e) => {
          //   e.element.classList.toggle('show-icon');
          // },
          onClick: (e) => {
            this.isFilterActive.noPositives =
              !this.isFilterActive.noPositives;

            e.element.classList.toggle(
              'show-icon',
              this.isFilterActive.noPositives
            );
            e.element.classList.toggle(
              'hide-icon',
              !this.isFilterActive.noPositives
            );

            this.applyFilters();
          },
        },
      });

      // En proceso
      toolbarItems.push({
        name: 'En proceso',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('inProcess'),
          icon: '././static/icons/ic_check.svg',
          width: 160,
          elementAttr: {
            class: `filtrosBotones ${
              this.isFilterActive.inProcess
                ? 'show-icon'
                : 'hide-icon'
            }`,
          },
          onClick: (e) => {
            this.isFilterActive.inProcess =
              !this.isFilterActive.inProcess;
            e.element.classList.toggle(
              'show-icon',
              this.isFilterActive.inProcess
            );
            e.element.classList.toggle(
              'hide-icon',
              !this.isFilterActive.inProcess
            );
            this.applyFilters();
          },
        },
      });

      // Terminada (status 3)
      toolbarItems.push({
        name: 'Terminada',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('finished'),
          icon: '././static/icons/ic_check.svg',
          width: 150,
          elementAttr: {
            class: `filtrosBotones ${
              this.isFilterActive.finished ? 'show-icon' : 'hide-icon'
            }`,
          },
          onClick: (e) => {
            this.isFilterActive.finished =
              !this.isFilterActive.finished;
            e.element.classList.toggle(
              'show-icon',
              this.isFilterActive.finished
            );
            e.element.classList.toggle(
              'hide-icon',
              !this.isFilterActive.finished
            );
            this.applyFilters();
          },
        },
      });

      // Cancelada (status 4)
      // toolbarItems.push({
      //   name: 'Cancelada',
      //   location: 'before',
      //   widget: 'dxButton',
      //   locateInMenu: 'never',
      //   options: {
      //     text: this.$t('SamplesList.statusList.cancelled'),
      //     icon: '././static/icons/ic_check.svg',
      //     width: 155,
      //     elementAttr: {
      //       class: `filtrosBotones ${
      //         this.isFilterActive.cancelled
      //           ? 'show-icon'
      //           : 'hide-icon'
      //       }`,
      //     },
      //     onClick: (e) => {
      //       this.isFilterActive.cancelled =
      //         !this.isFilterActive.cancelled;
      //       e.element.classList.toggle(
      //         'show-icon',
      //         this.isFilterActive.cancelled
      //       );
      //       e.element.classList.toggle(
      //         'hide-icon',
      //         !this.isFilterActive.cancelled
      //       );
      //       this.applyFilters();
      //     },
      //   },
      // });

      // Último mes
      toolbarItems.push({
        name: 'Último mes',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('lastMonth'),
          icon: '././static/icons/ic_check.svg',
          width: 143,
          elementAttr: {
            class: `filtrosBotones ${
              this.isFilterActive.lastMonth
                ? 'show-icon'
                : 'hide-icon'
            }`,
          },
          onClick: (e) => {
            this.isFilterActive.lastMonth =
              !this.isFilterActive.lastMonth;
            e.element.classList.toggle(
              'show-icon',
              this.isFilterActive.lastMonth
            );
            e.element.classList.toggle(
              'hide-icon',
              !this.isFilterActive.lastMonth
            );
            this.applyFilters();
          },
        },
      });

      // Registrar muestra
      toolbarItems.push({
        name: 'Registrar muestra',
        location: 'after',
        widget: 'dxButton',
        locateInMenu: 'never',
        cssClass: 'registrar-arriba',
        options: {
          text: this.$t('registerSample'),
          elementAttr: {
            class: 'botones-registrar',
          },
          onClick: () => {
            this.GoSampleRegister();
          },
        },
      });

      // Descargas
      toolbarItems.push({
        name: 'Descargas',
        location: 'after',
        widget: 'dxDropDownButton',
        locateInMenu: 'never',
        cssClass: 'btn-estado-custom descargar-abajo',
        options: {
          text: this.$t('download'),
          elementAttr: { class: 'btn-descargar' },

          items: [
            { key: 'excel', text: this.$t('excelDownload') },
            { key: 'pdf', text: this.$t('pdfReportDownload') },
          ],
          displayExpr: 'text',
          keyExpr: 'key',
          dropDownOptions: { width: 360 },
          splitButton: false,

          disabled: this.disableContext || this.isDownloadingReport,

          onItemClick: ({ itemData }) => {
            this.downloadSelected(itemData.key);
          },
        },
      });

      // Por estado (decorativo, lo dejo igual)
      toolbarItems.push({
        name: 'Por estado',
        location: 'after',
        widget: 'dxSelectBox',
        locateInMenu: 'never',
        visible: false,
        options: {
          placeholder: this.$t('byStatus'),
          elementAttr: {
            class: 'btn-estado',
          },
          dataSource: '',
          displayExpr: '',
          valueExpr: '',
          dropDownButtonTemplate() {
            return `
          <div class="dropdown-button">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 14.677C11.8795 14.677 11.7673 14.6577 11.6635 14.6192C11.5597 14.5807 11.4609 14.5147 11.3673 14.4212L6.873 9.927C6.73467 9.7885 6.66384 9.61441 6.6605 9.40475C6.65734 9.19525 6.72817 9.018 6.873 8.873C7.018 8.72816 7.19367 8.65575 7.4 8.65575C7.60634 8.65575 7.782 8.72816 7.927 8.873L12 12.9462L16.073 8.873C16.2115 8.73466 16.3856 8.66383 16.5953 8.6605C16.8048 8.65733 16.982 8.72816 17.127 8.873C17.2718 9.018 17.3443 9.19366 17.3443 9.4C17.3443 9.60633 17.2718 9.782 17.127 9.927L12.6328 14.4212C12.5391 14.5147 12.4403 14.5807 12.3365 14.6192C12.2327 14.6577 12.1205 14.677 12 14.677Z" fill="#00346A"/>
            </svg>
          </div>
      `;
          },
        },
      });
    },

    async download(e) {
      this.types = {
        food: this.$t('Food'),
        agronomy: this.$t('Agronomy'),
        environment: this.$t('Environment'),
        mining: this.$t('Mining'),
        healthAndSecurity: this.$t('Health and Security'),
      };

      if (e.row.data.atc_name === this.types.food) {
        const dxContextMenuEvent = {
          ...e.event,
          type: 'dxcontextmenu',
        };
        e.event.stopPropagation();

        const actionCellElement = getGridActionCellElement(e);

        if (!actionCellElement) return;

        trigger(actionCellElement, dxContextMenuEvent);
      } else {
        this.setIsDownloadingReport(true);
        this.notificationMessage = this.$t('downloading');

        try {
          const { sample_id, study_id, code } = e.row.data;
          const downloaded = await this.downloadDualReport({ sample_id, study_id, code });

          if (downloaded) {
            this.notificationMessage = this.$t('downloadedFile');
          } else {
            this.notificationMessage = '';
            this.$notifyDX(
              { message: this.$t('sampleHasNoReport'), width: 450 },
              'info',
              2000
            );
          }
        } catch (error) {
          this.notificationMessage = '';
          this.setIsDownloadingReport(false);
          this.$notifyDX(
            { message: this.$t('reportNotAvailable'), width: 550 },
            'error',
            NOTIFY_TIME_LONG
          );
        } finally {
          setTimeout(() => (this.notificationMessage = ''), 2000);
          this.setIsDownloadingReport(false);
        }
      }
    },

    isDisabled(e) {
      return e?.row?.data?.status_code !== '3';
    },

    addMenuItems(e) {
      this.types = {
        food: this.$t('Food'),
        agronomy: this.$t('Agronomy'),
        environment: this.$t('Environment'),
        mining: this.$t('Mining'),
        healthAndSecurity: this.$t('Health and Security'),
      };

      if (!e.row.data) return;

      if (
        e.column.name !== 'buttons' ||
        e.row.data.atc_name !== this.types.food
      ) {
        return;
      }

      if (e.target === 'content') {
        if (!e.items) e.items = [];

        e.items.push({
          icon: 'pdffile',
          text: this.$t('generalReport'),
          onItemClick: async () => {
            this.setIsDownloadingReport(true);
            this.notificationMessage = this.$t('downloading');

            try {
              const { sample_id, study_id, code } = e.row.data;
              const downloaded = await this.downloadDualReport({ sample_id, study_id, code });

              if (downloaded) {
                this.notificationMessage = this.$t('downloadedFile');
              } else {
                this.notificationMessage = '';
                this.$notifyDX(
                  {
                    message: this.$t('sampleHasNoReport'),
                    width: 450,
                  },
                  'info',
                  2000
                );
              }
            } catch (error) {
              this.$notifyDX(
                {
                  message: this.$t('reportNotAvailable'),
                  width: 550,
                },
                'error',
                NOTIFY_TIME_LONG
              );
            } finally {
              setTimeout(() => {
                this.notificationMessage = '';
              }, 2000);
              this.setIsDownloadingReport(false);
            }
          },
        });

        e.items.push({
          icon: 'pdffile',
          text: this.$t('tracesReport'),
          onItemClick: async () => {
            this.setIsDownloadingReport(true);

            this.notificationMessage = this.$t('downloading');

            try {
              const reportId = e.row.data.sample_id;
              this.setReportSampleId(reportId);
              this.setReportSampleCode(e.row.data.code);

              const report = await this.getReport(DOC_TYPE_TRACES);

              if (report.name && report.pdfB64) {
                const link = document.createElement('a');
                link.href = `data:application/octet-stream;base64,${report.pdfB64}`;
                link.download = `${e.row?.data?.code}_${DOC_TYPE_TRACES}.pdf`;
                link.click();

                URL.revokeObjectURL(link.href);

                this.notificationMessage = this.$t('downloadedFile');
              } else {
                this.notificationMessage = '';
                this.$notifyDX(
                  {
                    message: this.$t('sampleHasNoReport'),
                    width: 450,
                  },
                  'info',
                  2000
                );
              }
            } catch (error) {
              this.$notifyDX(
                {
                  message: this.$t('reportNotAvailable'),
                  width: 550,
                },
                'error',
                NOTIFY_TIME_LONG
              );
            } finally {
              setTimeout(() => {
                this.notificationMessage = '';
              }, 2000);
              this.setIsDownloadingReport(false);
            }
          },
          disabled: !e.row.data?.traces_report,
        });
      }
    },

    async viewAtc(e) {
      this.setSampleFetchedFromEtl(false);
      this.setLastRouteHome(true);
      this.setLastRouteGeotoolboxSamples(false);

      this.setCurrentSampleId(e.row.data.id);
      this.setReportSampleId(e.row.data.sample_id);
      this.setReportSampleCode(e.row.data.code);

      await this.getCurrentSample();

      await this.goToSampleDetailsByAtc(e.row.data.atc_name, e);
    },

    async goToSampleDetailsByAtc(atcName, e) {
      const atcKey = resolveAtcKey({
        atc_name: atcName,
        atc_code: e?.row?.data?.atc_code,
      });

      this.types = {
        food: this.$t('Food'),
        agronomy: this.$t('Agronomy'),
        environment: this.$t('Environment'),
        mining: this.$t('Mining'),
        healthAndSecurity: this.$t('Health and Security'),
      };

      if (atcKey === 'FOOD') {
        this.setCurrentFilter('foodDetails');
        this.setIsNoRp(e.row.data.value_report === 'CMA');
        this.setSelectedLimits([this.$t('positives')]);
        let positiveParameters = [];
        if (e.row.data.value_report !== 'CMA') {
          if (e.row.data.retailers) {
            const chains = [...e.row.data.retailers];
            // positiveParameters =
            //   e.row.data.positive_parameters_retailers || [];
            const rawParameters =
              e.row.data.positive_parameters_retailers || [];

            const uniqueParameters = Array.from(
              new Map(
                rawParameters.map((p) => [p.Parametro, p])
              ).values()
            );

            positiveParameters = uniqueParameters;
            const res = [];

            chains.forEach((chainData) => {
              chainData.Reglas.forEach((chain) => {
                if (
                  res.length > 0 &&
                  res.some(
                    (el) => el.Supermercado === chainData.Supermercado
                  )
                ) {
                  const indexRes = res.findIndex(
                    (el) => el.Supermercado === chainData.Supermercado
                  );
                  res[indexRes] = {
                    ...res[indexRes],
                    [`Regla ${chain.Regla}`]: chain.Regla,
                    [`Resultado ${chain.Regla}`]: chain.Resultado,
                    [`CodRegla ${chain.Regla}`]: chain.CodRegla,
                    Supermercado: chainData.Supermercado,
                    Confirmado: chainData.Confirmado,
                  };
                } else {
                  res.push({
                    [`Resultado ${chain.Regla}`]: chain.Resultado,
                    [`Regla ${chain.Regla}`]: chain.Regla,
                    [`CodRegla ${chain.Regla}`]: chain.CodRegla,
                    Supermercado: chainData.Supermercado,
                    Confirmado: chainData.Confirmado,
                    ...chain,
                  });
                }
              });
            });

            this.setEuropeanChains([
              {
                cadenas: res,
                parametrosPositivos: positiveParameters,
              },
            ]);
          } else {
            this.setEuropeanChains([]);
          }
        }

        this.pushRoute({
          name: this.$t('Food'),
          disabled: false,
          page: 'FoodSamplesPage',
          active: false,
        });

        this.pushRoute({
          name: this.$t('sampleSummary'),
          disabled: true,
          page: 'FoodSampleDetailPage',
          active: true,
        });

        this.$f7router.navigate(
          {
            name: 'FoodSampleDetailPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      } else if (atcKey === 'AGRONOMY') {
        this.setCurrentFilter('agronomy');

        this.pushRoute({
          name: this.$t('Agronomy'),
          disabled: false,
          page: 'AgronomySamplesPage',
          active: false,
        });

        this.pushRoute({
          name: this.$t('sampleSummary'),
          disabled: true,
          page: 'AgronomySamplesDetailsPage',
          active: true,
        });

        this.$f7router.navigate(
          {
            name: 'AgronomySamplesDetailsPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      } else if (atcKey === 'ENVIRONMENT') {
        this.setCurrentFilter('environmentDetails');

        this.pushRoute({
          name: this.$t('Environment'),
          disabled: false,
          page: 'EnvironmentSamplesPage',
          active: false,
        });

        this.pushRoute({
          name: this.$t('sampleSummary'),
          disabled: true,
          page: 'EnvironmentSamplesDetailsPage',
          active: true,
        });

        this.$f7router.navigate(
          {
            name: 'EnvironmentSamplesDetailsPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      } else if (atcKey === 'MINING') {
        this.setCurrentFilter('miningDetails');

        this.pushRoute({
          name: this.$t('Mining'),
          disabled: false,
          page: 'MiningSamplesPage',
          active: false,
        });

        this.pushRoute({
          name: this.$t('sampleSummary'),
          disabled: true,
          page: 'MiningSamplesDetailsPage',
          active: true,
        });

        this.$f7router.navigate(
          {
            name: 'MiningSamplesDetailsPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      } else if (atcKey === 'HEALTH_SECURITY') {
        this.setCurrentFilter('healthSecurityDetails');

        this.pushRoute({
          name: this.$t('Health and Security'),
          disabled: false,
          page: 'HealthSecuritySamplesPage',
          active: false,
        });

        this.pushRoute({
          name: this.$t('sampleSummary'),
          disabled: true,
          page: 'HealthSecuritySamplesDetailsPage',
          active: true,
        });

        this.$f7router.navigate(
          {
            name: 'HealthSecuritySamplesDetailsPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      }
    },

    onCellPrepared(e) {
      if (e.rowType === 'data') {
        if (!e.data.seen) {
          e.cellElement.classList.add('hasntBeenSeen');
        }
      }
    },

    onExporting(e) {
      this.notificationMessage = this.$t('samplesDownloading');
      e.component.beginUpdate();
      e.component.columnOption('ID', 'visible', true);
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet(
        `${`${this.$t(
          'agronomySample.exportExcelSampleTable'
        )}-${this.$t('Home')}`}`
      );

      exportDataGrid({
        component: e.component,
        worksheet,
      })
        .then(() => {
          return workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(
              new Blob([buffer], {
                type: 'application/octet-stream',
              }),
              `besafer-samples-${this.$helpers.getDateFormatedDayMonthYear(
                new Date()
              )}.xlsx`
            );
          });
        })
        .then(() => {
          e.component.columnOption('ID', 'visible', false);
          e.component.endUpdate();
          setTimeout(() => (this.notificationMessage = ''), 2000);
        });

      e.cancel = true;
    },

    async downloadSelected(type) {
      switch (type) {
        case 'pdf':
          await this.downloadPdf();
          break;
        case 'excel':
          this.onExporting(this.grid);
          break;
        default:
          break;
      }
    },

    async downloadPdf() {
      this.setDisableContext(true);

      try {
        this.notificationMessage = this.$t('downloading');
        const b64 = await this.getSamplesZip(
          this.getSamplesPageDownloadParams()
        );

        const link = document.createElement('a');
        link.href = `data:application/octet-stream;base64,${b64}`;
        link.download = `besafer-reports-PDF-${new Date().toLocaleString()}.zip`;
        link.click();

        URL.revokeObjectURL(link.href);
        this.notificationMessage = this.$t('downloadedFile');
      } catch (e) {
        this.notificationMessage = '';
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(
                JSON.parse(e.message).error
              )
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.setDisableContext(false);

        setTimeout(() => {
          this.notificationMessage = '';
        }, 2000);
      }
    },

    ...mapActions('filter', [
      'setCurrentFilter',
      'setCurrentSampleId',
    ]),
    ...mapActions('samples', [
      'getCurrentSample',
      'setReportSampleCode',
      'setDisableContext',
      'getSamplesWithParameters',
      'setAllFilterParams',
      'getSamplesZip',
      'setIsDownloadingReport',
      'setSampleFetchedFromEtl',
      'setLastRouteHome',
      'setLastRouteGeotoolboxSamples',
    ]),
    ...mapActions('samplesRegisterModule', [
      'RegisterInvoice',
      'setInvoiceSamples',
    ]),
    ...mapActions('homeSamples', ['setSamples']),
    ...mapActions('foodSamples', ['setIsNoRp', 'setEuropeanChains']),
    ...mapActions('foodDetailsFilter', ['setSelectedLimits']),
    ...mapActions('breadcrumb', ['pushRoute']),
  },
};
</script>

<style>
/* contenedor de la celda */
.custom-grid .combined-status-cell {
  display: flex;
  align-items: center;
}

/* separación entre el círculo y el texto */
.custom-grid .combined-status-cell .status-dot {
  margin-right: 3px;
  flex-shrink: 0;
}

.summary-header {
  display: flex;
  align-items: center;
  margin: 5px 5px !important;
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1 0 0;
}

.summary-icon {
  background-image: url('@/static/icons/ic_muestra.svg');
  display: flex;
  width: 32px;
  height: 32px;
  justify-content: center;
  align-items: center;
  aspect-ratio: 1/1;
}
.summary-text {
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%;
  color: #04509f;
}

/* .custom-grid-summary .custom-toolbar .dx-toolbar-before {
  padding-right: 0px !important;
  display: grid;
  grid-template-columns: 123px 152px 141px 152px 141px;
  grid-template-rows: repeat(2, 1fr);
  row-gap: 24px;
  align-items: center;
  box-sizing: border-box;
} */
.custom-grid-summary .custom-toolbar .dx-toolbar-before {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(115px, max-content));
  grid-auto-rows: 48px;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
}

.custom-grid-summary
  .custom-toolbar
  .dx-toolbar-before
  :nth-child(1) {
  grid-row: 1;
  grid-column: 1 / span 3;
}
.custom-grid-summary
  .custom-toolbar
  .dx-toolbar-before
  :nth-child(2) {
  grid-row: 2;
  grid-column: 1;
}
.custom-grid-summary
  .custom-toolbar
  .dx-toolbar-before
  :nth-child(3) {
  grid-row: 2;
  grid-column: 2;
}
.custom-grid-summary
  .custom-toolbar
  .dx-toolbar-before
  :nth-child(4) {
  grid-row: 2;
  grid-column: 3;
}
.custom-grid-summary
  .custom-toolbar
  .dx-toolbar-before
  :nth-child(5) {
  grid-row: 2;
  grid-column: 4;
}
.custom-grid-summary
  .custom-toolbar
  .dx-toolbar-before
  :nth-child(6) {
  grid-row: 2;
  grid-column: 5;
}
.custom-grid-summary
  .custom-toolbar
  .dx-toolbar-before
  :nth-child(7) {
  grid-row: 2;
  grid-column: 6;
}
.custom-grid-summary
  .custom-toolbar
  .dx-toolbar-before
  :nth-child(8) {
  grid-row: 2;
  grid-column: 7;
}
.custom-grid-summary
  .custom-toolbar
  .dx-toolbar-before
  :nth-child(9) {
  grid-row: 2;
  grid-column: 8;
}
/* Quitar el borde del botón "Registrar Muestra" */
.botones-registrar.dx-button .dx-button-content {
  /* border: none !important; */
  padding-top: 7px !important;
  padding-bottom: 7px !important;
}
.btn-descargar .dx-dropdownbutton-action .dx-button-content::after {
  content: '';
  display: inline-block;
  width: 24px;
  height: 24px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M12 14.677C11.8795 14.677 11.7673 14.6577 11.6635 14.6192C11.5597 14.5807 11.4609 14.5147 11.3673 14.4212L6.873 9.927C6.73467 9.7885 6.66384 9.61441 6.6605 9.40475C6.65734 9.19525 6.72817 9.018 6.873 8.873C7.018 8.72816 7.19367 8.65575 7.4 8.65575C7.60634 8.65575 7.782 8.72816 7.927 8.873L12 12.9462L16.073 8.873C16.2115 8.73466 16.3856 8.66383 16.5953 8.6605C16.8048 8.65733 16.982 8.72816 17.127 8.873C17.2718 9.018 17.3443 9.19366 17.3443 9.4C17.3443 9.60633 17.2718 9.782 17.127 9.927L12.6328 14.4212C12.5391 14.5147 12.4403 14.5807 12.3365 14.6192C12.2327 14.6577 12.1205 14.677 12 14.677Z' fill='%2300346A'/%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
  margin-left: 6px;
}
/* Ocultar la flecha nativa de DevExtreme SOLO en el botón Descargar */
.btn-descargar .dx-icon-spindown {
  display: none !important;
}

/* Registrar muestra ARRIBA (fila 1, columna 5) */
.registrar-arriba {
  grid-row: 1 !important;
  grid-column: 5 !important;
}

/* Descargar DEBAJO (fila 2, columna 5) */
.descargar-abajo {
  grid-row: 2 !important;
  grid-column: 5 !important;
}
/* Hacer dos filas en la parte derecha */
.custom-grid-summary .custom-toolbar .dx-toolbar-after {
  display: grid !important;
  grid-template-columns: 1fr;
  grid-template-rows: 48px 48px; /* altura exacta de las líneas */
  justify-items: end;
}

/* Registrar Muestra en fila 1 */
.custom-grid-summary .dx-toolbar-after .registrar-arriba {
  grid-row: 1 !important;
}

/* Descargar en fila 2 */
.custom-grid-summary .dx-toolbar-after .descargar-abajo {
  grid-row: 2 !important;
}
.custom-grid-summary
  .custom-toolbar
  .dx-toolbar-after
  .btn-estado-custom
  .dx-button-content {
  padding: 0 16px !important;
  padding-right: 8px !important;
}

@media (max-width: 1100px) {
  .custom-grid-summary .custom-toolbar .dx-toolbar-items-container {
    position: relative !important;
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before {
    display: grid !important;
    grid-template-columns: repeat(6, minmax(0, 1fr)) !important;
    grid-template-rows: 48px 48px !important;
    gap: 10px;
    position: relative !important;
    width: 100% !important;
    min-width: 0 !important;
    padding: 0 8px 0 0 !important;
    box-sizing: border-box;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before > * {
    grid-row: auto !important;
    grid-column: auto !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(1) {
    grid-row: 1 !important;
    grid-column: 1 / span 2 !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(2) {
    grid-row: 1 !important;
    grid-column: 3 !important;
    position: relative !important;
    left: -30px !important;
    top: -5px !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(3),
  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(4),
  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(5),
  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(6),
  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(7),
  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(8) {
    grid-row: 2 !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(3) {
    grid-column: 1 !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(4) {
    grid-column: 2 !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(5) {
    grid-column: 3 !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(6) {
    grid-column: 4 !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(7) {
    grid-column: 5 !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(8) {
    grid-column: 6 !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-after {
    display: flex !important;
    flex-wrap: nowrap !important;
    position: absolute !important;
    top: 0 !important;
    left: auto !important;
    right: 0 !important;
    gap: 8px;
    align-items: center !important;
    width: auto !important;
    min-width: max-content !important;
    padding: 0 !important;
    z-index: 2;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-after > * {
    grid-row: auto !important;
    grid-column: auto !important;
    flex: 0 0 auto !important;
  }
}

@media (max-width: 950px) {
  .custom-grid-summary .custom-toolbar .dx-toolbar-before {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    grid-template-rows: 48px 48px 48px 48px !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(1) {
    grid-row: 1 !important;
    grid-column: 1 / span 2 !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(2) {
    grid-row: 2 !important;
    grid-column: 1 !important;
    left: 0 !important;
    top: 0 !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(3),
  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(4),
  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(5) {
    grid-row: 3 !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(3) {
    grid-column: 1 !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(4) {
    grid-column: 2 !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(5) {
    grid-column: 3 !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(6),
  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(7),
  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(8) {
    grid-row: 4 !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(6) {
    grid-column: 1 !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(7) {
    grid-column: 2 !important;
  }

  .custom-grid-summary .custom-toolbar .dx-toolbar-before > :nth-child(8) {
    grid-column: 3 !important;
  }
}
/* OCULTAR errores internos del DataGrid */
.dx-datagrid .dx-error-row {
  display: none !important;
}
</style>
